import React from "react";
import { useTranslation } from "react-i18next";
import {
  Activity,
  CalendarCheck,
  CalendarClock,
  CalendarDays,
  CalendarPlus,
  CalendarRange,
  CalendarX,
  ChevronDown,
  ClipboardList,
  ClipboardPlus,
  Clock,
  FilePlus2,
  FileText,
  FlaskConical,
  FolderHeart,
  HeartPulse,
  Hospital,
  ListChecks,
  MapPin,
  Pill,
  ScanLine,
  ShieldAlert,
  Stethoscope,
  Syringe,
  UserSearch,
} from "lucide-react";

import "./QuickActions.css";

const ICONS = {
  activity: Activity,
  "calendar-check": CalendarCheck,
  "calendar-clock": CalendarClock,
  "calendar-days": CalendarDays,
  "calendar-plus": CalendarPlus,
  "calendar-range": CalendarRange,
  "calendar-x": CalendarX,
  "clipboard-list": ClipboardList,
  "clipboard-plus": ClipboardPlus,
  clock: Clock,
  "file-plus-2": FilePlus2,
  "file-text": FileText,
  "flask-conical": FlaskConical,
  "folder-heart": FolderHeart,
  "heart-pulse": HeartPulse,
  hospital: Hospital,
  "list-checks": ListChecks,
  "map-pin": MapPin,
  pill: Pill,
  "scan-line": ScanLine,
  "shield-alert": ShieldAlert,
  syringe: Syringe,
  "user-search": UserSearch,
};

const GROUP_ICONS = {
  appointments: CalendarDays,
  availability: CalendarRange,
  clinical: Stethoscope,
  events: CalendarClock,
  health: HeartPulse,
  locations: MapPin,
  medical_record: FolderHeart,
  medications: Pill,
  patients: UserSearch,
};

const normalizeLocale = (locale) => {
  const value = String(locale || "").toLowerCase();
  if (value.startsWith("ar")) return "ar";
  if (value.startsWith("en")) return "en";
  return "fr";
};

const getGroups = (capabilities, groups) => {
  if (Array.isArray(groups)) return groups;
  if (Array.isArray(capabilities?.groups)) return capabilities.groups;
  return [];
};

const readI18nObject = (t, key, lng) => {
  const value = t(key, { lng, returnObjects: true });
  return value && typeof value === "object" && !Array.isArray(value) ? value : {};
};

const actionCountLabel = (count, copy) =>
  count === 1 ? copy.oneAction : copy.manyActions?.replace("{{count}}", String(count));

const localizeGroupLabel = (group, labels) =>
  labels?.[group?.id] || group?.label || "";

const localizeActionText = (action, labels, field) => {
  const translation = labels?.[action?.id];
  if (!translation) return action?.[field] || "";
  return field === "description" ? translation[1] : translation[0];
};

const localizeBadge = (badge, labels) => {
  const normalized = String(badge || "").trim().toLowerCase();
  return labels?.[normalized] || badge;
};

export default function QuickActions({
  capabilities,
  groups,
  loading = false,
  error = null,
  onAction,
  onActionClick,
  role = "patient",
  locale = "fr",
  emptyLabel,
  className = "",
}) {
  const { t } = useTranslation("assistantAi");
  const currentLocale = normalizeLocale(locale);
  const fallbackCopy = readI18nObject(t, "quickActions.copy", "fr");
  const copy = {
    ...fallbackCopy,
    ...readI18nObject(t, "quickActions.copy", currentLocale),
  };
  const groupLabels = readI18nObject(t, "quickActions.groupLabels", currentLocale);
  const actionLabels = readI18nObject(t, "quickActions.actionLabels", currentLocale);
  const badgeLabels = readI18nObject(t, "quickActions.badgeLabels", currentLocale);
  const actionGroups = getGroups(capabilities, groups);
  const [openGroups, setOpenGroups] = React.useState(() => new Set(actionGroups.map((group) => group.id)));

  React.useEffect(() => {
    setOpenGroups((previous) => {
      const next = new Set(previous);
      if (next.size === 0) {
        actionGroups.forEach((group) => next.add(group.id));
        return next;
      }
      actionGroups.forEach((group) => {
        if (!next.has(group.id) && actionGroups.length <= 4) next.add(group.id);
      });
      return next;
    });
  }, [actionGroups]);

  const toggleGroup = (groupId) => {
    setOpenGroups((previous) => {
      const next = new Set(previous);
      if (next.has(groupId)) next.delete(groupId);
      else next.add(groupId);
      return next;
    });
  };

  if (loading) {
    return (
      <div className={`guided-quick-actions is-loading ${className}`} aria-live="polite">
        <span className="guided-quick-actions__loading-title" />
        <span className="guided-quick-actions__skeleton" />
        <span className="guided-quick-actions__skeleton" />
        <span className="guided-quick-actions__skeleton" />
      </div>
    );
  }

  if (error) {
    const errorText = currentLocale === "fr" && typeof error === "string" ? error : copy.unavailableText;
    return (
      <div className={`guided-quick-actions guided-quick-actions--state ${className}`} role="status">
        <strong>{copy.unavailableTitle}</strong>
        <span>{errorText}</span>
      </div>
    );
  }

  if (!actionGroups.length) {
    const emptyText = emptyLabel || copy.emptyText;
    return (
      <div className={`guided-quick-actions guided-quick-actions--state ${className}`} role="status">
        <strong>{copy.readyTitle}</strong>
        <span>{emptyText}</span>
      </div>
    );
  }

  return (
    <div className={`guided-quick-actions ${className}`} data-guided-ui="quick-actions" dir={currentLocale === "ar" ? "rtl" : "ltr"}>
      {actionGroups.map((group) => {
        const GroupIcon = GROUP_ICONS[group.id] || ListChecks;
        const actionCount = (group.actions || []).length;
        const groupLabel = localizeGroupLabel(group, groupLabels);

        return (
          <section
            className={`guided-quick-actions__group guided-quick-actions__group--${group.id || "default"} ${openGroups.has(group.id) ? "is-open" : "is-closed"}`}
            key={group.id}
          >
            <button
              type="button"
              className="guided-quick-actions__group-header"
              onClick={() => toggleGroup(group.id)}
              aria-expanded={openGroups.has(group.id)}
            >
              <span className="guided-quick-actions__group-icon" aria-hidden="true">
                <GroupIcon size={18} strokeWidth={2.2} />
              </span>
              <span className="guided-quick-actions__group-title-wrap">
                <span className="guided-quick-actions__group-title">{groupLabel}</span>
                <span className="guided-quick-actions__group-subtitle">
                  {actionCountLabel(actionCount, copy)}
                </span>
              </span>
              <span className="guided-quick-actions__group-count">{actionCount}</span>
              <ChevronDown className="guided-quick-actions__group-chevron" size={15} strokeWidth={2.4} aria-hidden="true" />
            </button>

            <div className="guided-quick-actions__list" hidden={!openGroups.has(group.id)}>
              {(group.actions || []).map((action) => {
                const Icon = ICONS[action.icon] || Stethoscope;
                const hasDirectRoute = Boolean(action.routeUrl || action.url || action.href);
                const variant = action.variant ? `guided-quick-actions__button--${action.variant}` : "";
                const actionLabel = localizeActionText(action, actionLabels, "label");
                const actionDescription = localizeActionText(action, actionLabels, "description");
                const actionBadge = localizeBadge(action.badge, badgeLabels);
                const localizedAction = {
                  ...action,
                  label: actionLabel,
                  description: actionDescription,
                  badge: actionBadge,
                };
                const localizedGroup = {
                  ...group,
                  label: groupLabel,
                };

                return (
                  <button
                    type="button"
                    className={`guided-quick-actions__button ${hasDirectRoute ? "guided-quick-actions__button--direct" : ""} ${variant}`}
                    key={action.id}
                    title={actionDescription || actionLabel}
                    onClick={() => {
                      onActionClick?.(action.id, localizedAction, localizedGroup, role);
                      onAction?.(localizedAction, localizedGroup);
                    }}
                  >
                    <span className="guided-quick-actions__icon" aria-hidden="true">
                      <Icon size={16} strokeWidth={2} />
                    </span>
                    <span className="guided-quick-actions__text">
                      <span className="guided-quick-actions__label-row">
                        <span className="guided-quick-actions__label">{actionLabel}</span>
                        {actionBadge && (
                          <span className="guided-quick-actions__badge">{actionBadge}</span>
                        )}
                      </span>
                      {actionDescription && (
                        <span className="guided-quick-actions__description">
                          {actionDescription}
                        </span>
                      )}
                    </span>
                  </button>
                );
              })}
            </div>
          </section>
        );
      })}
    </div>
  );
}
